import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import { UNASSIGNED_PLACEHOLDER } from "../constants";
import { isMergeSimilarActive } from "../predicates/isMergeSimilarActive";
import { sortHorizontalAxis } from "../sort/horizontalAxisSort";
import type { CustomTimeGroupsConfig, HorizontalLayout, PrintoutColumn, PrintoutColumnType, ScheduleRow } from "../types";
import { getRowDisplayValue } from "../util/displayValue";
import { buildShiftTimeDisplayAliasMap } from "./buildShiftTimeAlias";
import { buildCustomTimeGroupBands } from "./customTimeGroups";

interface BuildHorizontalLayoutArgs {
	rows: ScheduleRow[];
	columnGroupingBy: PrintoutColumnType;
	enabledColumns: PrintoutColumn[];
	rowGroupingBy: PrintoutColumnType[];
	timezone: string;
	department: Department;
	jobManagementEnabled: boolean;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	mergeSimilarShiftTimes: boolean | undefined;
	customTimeGroups: CustomTimeGroupsConfig;
	/** Heading of the band for shifts that cover no custom time group; read only when custom groups are on. */
	customTimeGroupsOtherLabel: string;
}

/**
 * Compute the horizontal-pivot layout for one sheet (mapping §8.2).
 *
 * Outputs:
 * - `anchorColumns` — the row-grouping columns that stay on the left (excluding the pivot column itself)
 * - `subColumns` — the non-grouping columns that get repeated under each horizontal band
 * - `horizontalValues` — the distinct pivot-axis values in display order
 * - `hValAliasMap` — when `Merge similar shift times` fires for this configuration, maps secondary
 *   shift-time display values to the dominant variant (mapping §8.2 Case A); empty otherwise
 *
 * The axis ordering uses the shared `compareValues` via `sortHorizontalAxis` — no bespoke
 * per-column-kind comparator.
 */
export const buildHorizontalLayout = (args: BuildHorizontalLayoutArgs): HorizontalLayout => {
	const {
		rows,
		columnGroupingBy,
		enabledColumns,
		rowGroupingBy,
		timezone,
		department,
		jobManagementEnabled,
		sortBy,
		customStringOrderMap,
		mergeSimilarShiftTimes,
		customTimeGroups,
		customTimeGroupsOtherLabel,
	} = args;

	const anchorColumns = enabledColumns.filter((col) => rowGroupingBy.includes(col.type) && col.type !== columnGroupingBy);
	const subColumns = enabledColumns.filter((col) => !rowGroupingBy.includes(col.type) && col.type !== columnGroupingBy);

	const customGroupsActive = columnGroupingBy === "shiftTime" && customTimeGroups.enabled;
	if (customGroupsActive) {
		// A custom group's heading is its name or range, not the shift's time, so each staff cell keeps
		// its own Shift Time column in the user's column order.
		const customGroupSubColumns = enabledColumns.filter((col) => !rowGroupingBy.includes(col.type));
		if (!customTimeGroupsOtherLabel) throw new Error("Custom time groups require a translated unmatched-group label");
		// DEFAULT: Shift Time columns keep the existing chronological ascending order when no enabled
		// Shift Time sort is configured; an explicit descending sort reverses configured groups only.
		const sortDirection = sortBy?.find((column) => column.enabled && column.type === "shiftTime")?.sortDirection === "desc" ? "desc" : "asc";
		const bands = buildCustomTimeGroupBands({
			rows,
			groups: customTimeGroups.groups,
			timezone,
			dayStart: department.dayStart,
			sortDirection,
			otherLabel: customTimeGroupsOtherLabel,
		});
		const horizontalKeysByRow = new Map<ScheduleRow, string[]>();
		for (const band of bands) {
			for (const row of band.rows) {
				const existing = horizontalKeysByRow.get(row);
				if (existing) existing.push(band.key);
				else horizontalKeysByRow.set(row, [band.key]);
			}
		}
		return {
			anchorColumns,
			subColumns: customGroupSubColumns,
			horizontalValues: bands.map((band) => band.key),
			horizontalValueLabels: new Map(bands.map((band) => [band.key, band.label])),
			horizontalKeysByRow,
			hValAliasMap: new Map<string, string>(),
		};
	}

	const mergeActive = isMergeSimilarActive({ mergeSimilarShiftTimes, columnGroupingBy, rowGroupingBy });
	const hValAliasMap: Map<string, string> =
		mergeActive && columnGroupingBy === "shiftTime" ? buildShiftTimeDisplayAliasMap(rows, timezone) : new Map<string, string>();

	// Map each (possibly alias-resolved) horizontal display value to a representative DateTime
	// — used by `sortHorizontalAxis` to drive chronological ordering for shift-time pivots.
	const shiftTimeReprs = new Map<string, DateTime>();
	const distinctValues = new Set<string>();
	const horizontalKeysByRow = new Map<ScheduleRow, string[]>();
	for (const row of rows) {
		const raw = getRowDisplayValue({ row, colType: columnGroupingBy, timezone, jobManagementEnabled, department }) || UNASSIGNED_PLACEHOLDER;
		const resolved = hValAliasMap.get(raw) ?? raw;
		horizontalKeysByRow.set(row, [resolved]);
		if (!distinctValues.has(resolved)) {
			distinctValues.add(resolved);
			if (columnGroupingBy === "shiftTime") {
				shiftTimeReprs.set(resolved, row.shiftStart);
			}
		}
	}

	const horizontalValues = sortHorizontalAxis({
		values: [...distinctValues],
		columnGroupingBy,
		shiftTimeReprs: columnGroupingBy === "shiftTime" ? shiftTimeReprs : undefined,
		sortBy,
		customStringOrderMap,
	});

	return {
		anchorColumns,
		subColumns,
		horizontalValues,
		horizontalValueLabels: new Map(horizontalValues.map((value) => [value, value])),
		horizontalKeysByRow,
		hValAliasMap,
	};
};

/**
 * Compute the flat ExcelJS-column list for a pivot sheet (anchor columns + sub-columns × bands).
 *
 * Used for worksheet width setup before the renderer starts emitting cells.
 */
export const flattenPivotColumns = (layout: HorizontalLayout): PrintoutColumn[] => {
	return [...layout.anchorColumns, ...layout.horizontalValues.flatMap(() => layout.subColumns)];
};
